// Recipe Module Phase 3 (docs/RECIPE_MODULE.md) — company-wide Knowledge Base: upload cookbook/
// reference PDFs, chat with an AI grounded in them (falls back to web search when the books don't
// cover a question), and generate a sample recipe draft. Mirrors the Maintenance Module's
// ManualTab.js/KnowledgeChatTab.js patterns, generalized from one asset's manual to a whole
// company-wide document library.
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import knowledgeBaseService from '../services/knowledgeBaseService';
import ConfirmModal from '../components/ConfirmModal';
import MarkdownMessage from '../components/MarkdownMessage';
import AiProgressIndicator from './maintenance/AiProgressIndicator';
import { usePermissions } from '../context/PermissionContext';

const DOCUMENT_MAX_BYTES = 32 * 1024 * 1024;
const CHAT_STAGES = ['Reading your books...', 'Thinking it through...', 'Almost there...'];

function fmtSize(bytes) {
  if (bytes == null) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function LibraryTab({ canManage }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showUpload, setShowUpload] = useState(false);
  const [title, setTitle] = useState('');
  const [author, setAuthor] = useState('');
  const [file, setFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef(null);
  const [reindexingId, setReindexingId] = useState(null);
  const [reindexProgress, setReindexProgress] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setDocuments(await knowledgeBaseService.listDocuments());
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load documents.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.type !== 'application/pdf') { setError('Only PDF files are accepted.'); return; }
    if (f.size > DOCUMENT_MAX_BYTES) { setError('File exceeds the 32 MB limit.'); return; }
    setError('');
    setFile(f);
    if (!title.trim()) setTitle(f.name.replace(/\.pdf$/i, ''));
  };

  const handleUpload = async () => {
    if (!file || !title.trim()) return setError('A title and a PDF file are both required.');
    setUploading(true);
    setError('');
    setUploadProgress('Uploading PDF…');
    try {
      const { uploadUrl, key } = await knowledgeBaseService.getDocumentUploadUrl(file.type, file.size);
      await knowledgeBaseService.uploadToPresignedUrl(uploadUrl, file);
      await knowledgeBaseService.saveDocument(
        { s3Key: key, fileName: file.name, sizeBytes: file.size, title: title.trim(), author: author.trim() || null },
        setUploadProgress,
      );
      setShowUpload(false);
      setTitle(''); setAuthor(''); setFile(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to upload document.');
    } finally {
      setUploading(false);
      setUploadProgress('');
    }
  };

  const handleReindex = async (doc) => {
    setReindexingId(doc.id);
    setReindexProgress('');
    setError('');
    try {
      await knowledgeBaseService.reindexDocument(doc.id, setReindexProgress);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to reindex document.');
    } finally {
      setReindexingId(null);
      setReindexProgress('');
    }
  };

  const [viewingId, setViewingId] = useState(null);

  const handleView = async (doc) => {
    setViewingId(doc.id);
    setError('');
    try {
      const url = await knowledgeBaseService.getDocumentDownloadUrl(doc.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to open document.');
    } finally {
      setViewingId(null);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await knowledgeBaseService.deleteDocument(deleteTarget.id);
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete document.');
      setDeleteTarget(null);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      {error && <div className="alert alert-danger py-2">{error}</div>}

      {canManage && (
        <div className="mb-3">
          {!showUpload ? (
            <button className="btn btn-outline-primary btn-sm" onClick={() => setShowUpload(true)}>+ Upload a Book / PDF</button>
          ) : (
            <div className="card" style={{ border: '1px solid #e9ecef' }}>
              <div className="card-body">
                <div className="row g-2 mb-2">
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Title <span className="text-danger">*</span></label>
                    <input className="form-control form-control-sm" value={title} onChange={e => setTitle(e.target.value)} placeholder="e.g. The Bread Baker's Apprentice" />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label small fw-semibold">Author</label>
                    <input className="form-control form-control-sm" value={author} onChange={e => setAuthor(e.target.value)} placeholder="optional" />
                  </div>
                </div>
                <div className="mb-2">
                  <label className="form-label small fw-semibold">PDF file (max 32 MB)</label>
                  <input ref={fileInputRef} type="file" accept="application/pdf" className="form-control form-control-sm" onChange={handleFileChange} />
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button className="btn btn-primary btn-sm" onClick={handleUpload} disabled={uploading || !file || !title.trim()}>
                    {uploading ? <><span className="spinner-border spinner-border-sm me-1" />Processing…</> : 'Upload'}
                  </button>
                  <button className="btn btn-outline-secondary btn-sm" onClick={() => { setShowUpload(false); setFile(null); }} disabled={uploading}>Cancel</button>
                  {uploading && uploadProgress && <span className="text-muted small">{uploadProgress}</span>}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center text-muted py-4"><div className="spinner-border spinner-border-sm me-2" />Loading…</div>
      ) : documents.length === 0 ? (
        <div className="text-center text-muted py-4">No books/documents uploaded yet.</div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm">
            <thead className="table-light">
              <tr><th>Title</th><th>Author</th><th>File</th><th className="text-end">Size</th><th>Index</th><th></th></tr>
            </thead>
            <tbody>
              {documents.map(d => {
                const isReindexing = reindexingId === d.id;
                return (
                  <tr key={d.id}>
                    <td className="fw-semibold">{d.title}</td>
                    <td>{d.author || <span className="text-muted">—</span>}</td>
                    <td className="text-muted">{d.fileName}</td>
                    <td className="text-end">{fmtSize(d.sizeBytes)}</td>
                    <td>
                      {isReindexing ? (
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          <span className="spinner-border spinner-border-sm me-1" style={{ width: 11, height: 11 }} />
                          {reindexProgress || 'Indexing…'}
                        </span>
                      ) : d.indexStatus === 'ready' ? (
                        <span className="badge bg-success-subtle text-success border border-success-subtle">✅ Indexed ({d.chunkCount} chunks)</span>
                      ) : d.indexStatus === 'indexing' ? (
                        <span className="badge bg-info-subtle text-info border border-info-subtle">⏳ Indexing…</span>
                      ) : d.indexStatus === 'failed' ? (
                        <span className="badge bg-danger-subtle text-danger border border-danger-subtle" title={d.indexError || ''}>❌ Failed</span>
                      ) : (
                        <span className="badge bg-secondary-subtle text-secondary border border-secondary-subtle">Not indexed yet</span>
                      )}
                    </td>
                    <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-outline-secondary me-1" onClick={() => handleView(d)} disabled={viewingId === d.id}>
                        {viewingId === d.id ? 'Opening…' : '📄 View'}
                      </button>
                      {canManage && d.indexStatus !== 'indexing' && (
                        <button className="btn btn-sm btn-outline-primary me-1" onClick={() => handleReindex(d)} disabled={isReindexing}>
                          🔄 {d.indexStatus === 'ready' ? 'Reindex' : 'Index now'}
                        </button>
                      )}
                      {canManage && (
                        <button className="btn btn-sm btn-outline-danger" onClick={() => setDeleteTarget(d)}>Delete</button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmModal
        show={!!deleteTarget}
        title="Delete document?"
        message={`This permanently removes "${deleteTarget?.title}" from the Knowledge Base. Past chat answers that cited it are not affected.`}
        confirmLabel={deleting ? 'Deleting…' : 'Delete'}
        variant="danger"
        onConfirm={handleDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}

function AskAiTab() {
  const [sessionId, setSessionId] = useState(() => crypto.randomUUID());
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const bottomRef = useRef(null);

  useEffect(() => {
    knowledgeBaseService.getChatHistory(sessionId).then(setMessages).catch(() => setMessages([]));
  }, [sessionId]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [messages]);

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setMessages(prev => [...prev, { role: 'user', content: q }]);
    setInput('');
    setSending(true);
    setError('');
    try {
      const data = await knowledgeBaseService.sendChatMessage(q, sessionId);
      setMessages(prev => [...prev, {
        role: 'assistant', content: data.answer, usedDocuments: data.usedDocuments, usedWebSearch: data.usedWebSearch,
        sources: data.sources || [],
      }]);
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to answer.');
    } finally {
      setSending(false);
    }
  };

  const newChat = () => { setSessionId(crypto.randomUUID()); setMessages([]); setError(''); };

  const [choosingIdx, setChoosingIdx] = useState(null);
  const [convertingIdx, setConvertingIdx] = useState(null);
  const [convertProgress, setConvertProgress] = useState('');
  const navigate = useNavigate();

  const handleConvert = async (idx, recipeType) => {
    setConvertingIdx(idx);
    setChoosingIdx(null);
    setError('');
    try {
      const recipe = await knowledgeBaseService.convertTextToRecipe(recipeType, messages[idx].content, setConvertProgress);
      navigate(recipeType === 'dough' ? '/recipes/dough/new' : '/recipes/percentage/new', { state: { generatedDraft: recipe } });
    } catch (err) {
      setError(err.message || 'Failed to convert this into a recipe.');
      setConvertingIdx(null);
    }
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div className="text-muted small">Ask anything about your uploaded books — falls back to web search when they don't cover it.</div>
        <button className="btn btn-sm btn-outline-secondary" onClick={newChat}>+ New Chat</button>
      </div>

      <div className="card" style={{ border: '1px solid #e9ecef' }}>
        <div className="card-body" style={{ minHeight: 320, maxHeight: 480, overflowY: 'auto' }}>
          {messages.length === 0 && <div className="text-muted text-center py-4">No messages yet — ask a question below.</div>}
          {messages.map((m, idx) => (
            <div key={idx} className={`mb-3 d-flex ${m.role === 'user' ? 'justify-content-end' : 'justify-content-start'}`}>
              <div className="p-2 rounded" style={{ maxWidth: '85%', background: m.role === 'user' ? '#0d6efd' : '#f1f3f5', color: m.role === 'user' ? '#fff' : '#212529' }}>
                {m.role === 'user' ? m.content : <MarkdownMessage content={m.content} />}
                {m.role === 'assistant' && (m.usedDocuments || m.usedWebSearch) && (
                  <div className="mt-1" style={{ fontSize: 11, opacity: 0.8 }}>
                    {m.usedDocuments && m.sources?.length > 0 && (
                      <div>📚 {m.sources.map((s, i) => (
                        <span key={i}>{i > 0 && ', '}{s.title}{s.pageNumber ? ` (p. ${s.pageNumber})` : ''}</span>
                      ))}</div>
                    )}
                    {m.usedWebSearch && <span>🌐 web search</span>}
                  </div>
                )}
                {m.role === 'assistant' && (
                  <div className="mt-2">
                    {convertingIdx === idx ? (
                      <div className="text-muted" style={{ fontSize: 12 }}>{convertProgress || 'Converting…'}</div>
                    ) : choosingIdx === idx ? (
                      <div className="d-flex flex-wrap gap-1">
                        <span className="text-muted small me-1">Add as:</span>
                        <button className="btn btn-sm btn-outline-dark py-0" onClick={() => handleConvert(idx, 'dough')}>🍞 Dough</button>
                        <button className="btn btn-sm btn-outline-dark py-0" onClick={() => handleConvert(idx, 'percentage')}>🥘 Percentage</button>
                        <button className="btn btn-sm btn-outline-secondary py-0" onClick={() => setChoosingIdx(null)}>✕</button>
                      </div>
                    ) : (
                      <button className="btn btn-sm btn-outline-success py-0" onClick={() => setChoosingIdx(idx)}>➕ Add to your recipes</button>
                    )}
                  </div>
                )}
              </div>
            </div>
          ))}
          <div ref={bottomRef} />
        </div>
        <div className="card-footer bg-white">
          {error && <div className="alert alert-danger py-1 small mb-2">{error}</div>}
          <AiProgressIndicator active={sending} messages={CHAT_STAGES} />
          <div className="input-group mt-2">
            <input className="form-control" value={input} placeholder="e.g. What's a good hydration for a focaccia?"
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }} />
            <button className="btn btn-primary" onClick={send} disabled={sending || !input.trim()}>Send</button>
          </div>
        </div>
      </div>
      <div className="text-muted mt-2" style={{ fontSize: 11 }}>
        Always verify food-safety-critical information (temperatures, allergens, hold times) against your actual SQF plan or a qualified professional.
      </div>
    </div>
  );
}

export default function KnowledgeBaseHubPage() {
  const navigate = useNavigate();
  const { role } = usePermissions();
  const canManage = role === 'admin' || role === 'superadmin' || role === 'qa';
  const [tab, setTab] = useState('library');

  return (
    <div className="container mt-4 pb-5">
      <div className="d-flex flex-column flex-md-row justify-content-md-between align-items-start align-items-md-center mb-4 p-4 rounded gap-3"
        style={{ background: 'linear-gradient(135deg, #6f42c1 0%, #59359c 100%)' }}>
        <div>
          <h4 className="mb-0 text-white">📚 Recipe Knowledge Base</h4>
          <small className="text-white" style={{ opacity: 0.85 }}>Upload cookbooks/reference PDFs and ask an AI that's read them.</small>
        </div>
        <button className="btn btn-light btn-sm fw-semibold" onClick={() => navigate('/recipes')}>← Back to Library</button>
      </div>

      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button className={`nav-link ${tab === 'library' ? 'active' : ''}`} onClick={() => setTab('library')}>📖 Library</button>
        </li>
        <li className="nav-item">
          <button className={`nav-link ${tab === 'ask-ai' ? 'active' : ''}`} onClick={() => setTab('ask-ai')}>💬 Ask AI</button>
        </li>
      </ul>

      {tab === 'library' ? <LibraryTab canManage={canManage} /> : <AskAiTab />}
    </div>
  );
}
